import { ICompanyOnboardingStatus } from '../resources/interfaces';

export interface ActionTask {
  id: string;
  title: string;
  message: string;
  completed: boolean;
  action?: {
    label: string;
    onClick: () => void;
  } | null;
  actions?: Array<{
    label: string;
    onClick: () => void;
  }>;
}

/**
 * Helper to get completion status from an action object
 * Checks 'completed' first, then falls back to 'configured'
 */
export const getCompletionStatus = (item: any): boolean => {
  return item?.completed ?? item?.configured ?? false;
};

/**
 * Generate user-friendly titles for action messages
 */
export const getActionTitle = (message: string): string => {
  const lowerMessage = message?.toLowerCase() || '';
  if (lowerMessage.includes('payout account')) return 'Set Up Payout Account';
  if (lowerMessage.includes('security')) return 'Configure Security Settings';
  if (lowerMessage.includes('notification')) return 'Set Up Notifications';
  if (lowerMessage.includes('terms and conditions')) return 'Accept Terms & Conditions';
  if (lowerMessage.includes('insurance') || lowerMessage.includes('insurer')) return 'Add Insurance Company';
  if (lowerMessage.includes('policies') || lowerMessage.includes('policy')) return 'Create Your First Policy';
  if (lowerMessage.includes('team') || lowerMessage.includes('staff') || lowerMessage.includes('member')) return 'Invite Team Members';
  if (lowerMessage.includes('company setup')) return 'Complete Company Profile';
  return 'Complete Setup Step';
};

/**
 * Get multiple actions for a task (used for policy creation)
 */
export const getMultipleActions = (
  message: string,
  navigate: (path: string) => void,
  getDashboardBase: () => string
): Array<{ label: string; onClick: () => void }> | null => {
  const lowerMessage = message?.toLowerCase() || '';
  
  if (lowerMessage.includes('policies') || lowerMessage.includes('policy')) {
    return [
      {
        label: 'Motor Policy',
        onClick: () => navigate(`${getDashboardBase()}/policies?type=motor`)
      },
      {
        label: 'Non-Motor Policy',
        onClick: () => navigate(`${getDashboardBase()}/policies?type=non-motor`)
      }
    ];
  }
  
  return null;
};

/**
 * Map action messages to navigation routes and labels
 */
export const getActionDetails = (
  message: string,
  navigate: (path: string) => void,
  getDashboardBase: () => string
): { label: string; onClick: () => void } | null => {
  const lowerMessage = message?.toLowerCase() || '';
  
  if (lowerMessage.includes('payout account')) {
    return {
      label: 'Set Up Now',
      onClick: () => navigate(`${getDashboardBase()}/settings?tab=settlementAccount`)
    };
  }
  if (lowerMessage.includes('security')) {
    return {
      label: 'Configure Now',
      onClick: () => navigate(`${getDashboardBase()}/settings?tab=security`)
    };
  }
  if (lowerMessage.includes('notification')) {
    return {
      label: 'Set Up Now',
      onClick: () => navigate(`${getDashboardBase()}/settings?tab=notifications`)
    };
  }
  if (lowerMessage.includes('terms and conditions')) {
    return {
      label: 'Review Now',
      onClick: () => navigate(`${getDashboardBase()}/settings?tab=termsandconditions`)
    };
  }
  if (lowerMessage.includes('insurance') || lowerMessage.includes('insurer')) {
    return {
      label: 'Add Insurer',
      onClick: () => navigate(`${getDashboardBase()}/settings?tab=insuranceCompanies`)
    };
  }
  if (lowerMessage.includes('policies') || lowerMessage.includes('policy')) {
    return null;
  }
  if (lowerMessage.includes('team') || lowerMessage.includes('staff') || lowerMessage.includes('member')) {
    return {
      label: 'Invite Team',
      onClick: () => navigate(`${getDashboardBase()}/settings?tab=users`)
    };
  }
  if (lowerMessage.includes('company setup') || lowerMessage.includes('company profile')) {
    return {
      label: 'Complete Profile',
      onClick: () => navigate(`${getDashboardBase()}/settings?tab=organization`)
    };
  }

  return null;
};

/**
 * Process onboarding status into a list of action tasks
 * Handles deduplication between action objects and pendingActions array
 */
export const processOnboardingTasks = (
  onboardingStatus: ICompanyOnboardingStatus | null | undefined,
  navigate: (path: string) => void,
  getDashboardBase: () => string
): ActionTask[] => {
  const tasks: ActionTask[] = [];
  
  if (!onboardingStatus) return tasks;

  // 1. Add Insurance Company
  if (onboardingStatus.insuranceCompanyConfig?.message) {
    tasks.push({
      id: 'insurance-company-config',
      title: getActionTitle(onboardingStatus.insuranceCompanyConfig.message),
      message: onboardingStatus.insuranceCompanyConfig.message,
      completed: onboardingStatus.insuranceCompanyConfig.completed || false,
      action: getActionDetails(onboardingStatus.insuranceCompanyConfig.message, navigate, getDashboardBase)
    });
  }

  // 2. Set Up Notifications
  if (onboardingStatus.notificationSettings?.message) {
    tasks.push({
      id: 'notification-settings',
      title: getActionTitle(onboardingStatus.notificationSettings.message),
      message: onboardingStatus.notificationSettings.message,
      completed: getCompletionStatus(onboardingStatus.notificationSettings),
      action: getActionDetails(onboardingStatus.notificationSettings.message, navigate, getDashboardBase)
    });
  }

  // 3. Invite Team Members
  if (onboardingStatus.team?.message) {
    tasks.push({
      id: 'team',
      title: getActionTitle(onboardingStatus.team.message),
      message: onboardingStatus.team.message,
      completed: onboardingStatus.team.completed || false,
      action: getActionDetails(onboardingStatus.team.message, navigate, getDashboardBase)
    });
  }

  // 4. Create Your First Policy
  if (onboardingStatus.policyData?.message) {
    const completed = onboardingStatus.policyData.completed || false;
    const multipleActions = !completed ? getMultipleActions(onboardingStatus.policyData.message, navigate, getDashboardBase) : null;
    
    tasks.push({
      id: 'policy-data',
      title: getActionTitle(onboardingStatus.policyData.message),
      message: onboardingStatus.policyData.message,
      completed,
      action: getActionDetails(onboardingStatus.policyData.message, navigate, getDashboardBase),
      actions: multipleActions || undefined
    });
  }

  // Commented out tasks
  // Process security settings
  // if (onboardingStatus.securitySettings?.message) {
  //   tasks.push({
  //     id: 'security-settings',
  //     title: getActionTitle(onboardingStatus.securitySettings.message),
  //     message: onboardingStatus.securitySettings.message,
  //     completed: getCompletionStatus(onboardingStatus.securitySettings),
  //     action: getActionDetails(onboardingStatus.securitySettings.message, navigate, getDashboardBase)
  //   });
  // }

  // Process payout account
  // if (onboardingStatus.payoutAccount?.message) {
  //   tasks.push({
  //     id: 'payout-account',
  //     title: getActionTitle(onboardingStatus.payoutAccount.message),
  //     message: onboardingStatus.payoutAccount.message,
  //     completed: getCompletionStatus(onboardingStatus.payoutAccount),
  //     action: getActionDetails(onboardingStatus.payoutAccount.message, navigate, getDashboardBase)
  //   });
  // }

  // Process company info
  // if (onboardingStatus.companyInfo?.message) {
  //   tasks.push({
  //     id: 'company-info',
  //     title: getActionTitle(onboardingStatus.companyInfo.message),
  //     message: onboardingStatus.companyInfo.message,
  //     completed: onboardingStatus.companyInfo.completed || false,
  //     action: getActionDetails(onboardingStatus.companyInfo.message, navigate, getDashboardBase)
  //   });
  // }

  // Process pending actions array (skip duplicates already added from objects)
  // const pendingActions = onboardingStatus.pendingActions || [];
  // const existingMessages = new Set(tasks.map(t => t.message));
  // 
  // pendingActions.forEach((actionMessage, index) => {
  //   // Skip if this message is already in tasks from an object
  //   if (!existingMessages.has(actionMessage)) {
  //     const multipleActions = getMultipleActions(actionMessage, navigate, getDashboardBase);
  //     
  //     tasks.push({
  //       id: `pending-action-${index}`,
  //       title: getActionTitle(actionMessage),
  //       message: actionMessage,
  //       completed: false,
  //       action: getActionDetails(actionMessage, navigate, getDashboardBase),
  //       actions: multipleActions || undefined
  //     });
  //   }
  // });

  // Return tasks in the specified order (no sorting needed as they're added in order)
  return tasks;
};

/**
 * Calculate total number of tasks from onboarding status
 * Handles deduplication between action objects and pendingActions
 */
export const calculateTotalTasks = (
  onboardingStatus: ICompanyOnboardingStatus | null | undefined
): number => {
  if (!onboardingStatus) return 0;
  
  const messages = new Set<string>();
  
  // Count only the 4 active tasks
  if (onboardingStatus.insuranceCompanyConfig?.message) {
    messages.add(onboardingStatus.insuranceCompanyConfig.message);
  }
  if (onboardingStatus.notificationSettings?.message) {
    messages.add(onboardingStatus.notificationSettings.message);
  }
  if (onboardingStatus.team?.message) {
    messages.add(onboardingStatus.team.message);
  }
  if (onboardingStatus.policyData?.message) {
    messages.add(onboardingStatus.policyData.message);
  }
  
  // Commented out tasks
  // if (onboardingStatus.securitySettings?.message) {
  //   messages.add(onboardingStatus.securitySettings.message);
  // }
  // if (onboardingStatus.payoutAccount?.message) {
  //   messages.add(onboardingStatus.payoutAccount.message);
  // }
  // if (onboardingStatus.companyInfo?.message) {
  //   messages.add(onboardingStatus.companyInfo.message);
  // }
  
  // Add pending actions that aren't already counted
  // const pendingActions = onboardingStatus.pendingActions || [];
  // pendingActions.forEach(action => {
  //   messages.add(action);
  // });
  
  return messages.size;
};
